import React from 'react';
import clsx from 'clsx';
import Typography from '@smart-tv/components/typography/typography';

import {
  ArrowPressHandler,
  FocusHandler,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';

import styles from './icon-button.module.scss';

export interface ButtonProps {
  focusKey: string | undefined;
}

export type ClassTriggers = 'push';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The onEnterPress callback is fired when the enter button is clicked, while the button is in focus
   */
  onEnterPress?: () => void;
  onArrowPress?: ArrowPressHandler<ButtonProps>;
  focusKey?: string;
  header?: boolean;
  activePage?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'lg' | 'xl';
  icon?: React.ReactNode;
  staticText?: boolean;
  classTrigger?: ClassTriggers;
  onNavFocus?: FocusHandler;
  onNavBlur?: FocusHandler;
  background?: boolean;
  gutterTop?: boolean;
  gutterBottom?: boolean;
  gutterLeft?: boolean;
  gutterRight?: boolean;
}

export const IconButton: React.FC<Props> = (props: Props) => {
  const {
    children,
    type = 'button',
    onEnterPress,
    focusKey: focusKeyParam,
    onArrowPress,
    activePage,
    disabled,
    size = 'lg',
    icon,
    classTrigger,
    onNavFocus,
    onNavBlur,
    background = false,
    gutterTop = false,
    gutterBottom = false,
    gutterLeft = false,
    gutterRight = false,
    className,
    ...rest
  } = props;

  const [animating, setAnimating] = React.useState(false);

  const handleEnterPress = React.useCallback(
    () => {
      setAnimating(true);
      ref.current.click();

      onEnterPress?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { ref, focused } = useFocusable({
    onFocus: onNavFocus,
    onBlur: onNavBlur,
    onEnterPress: handleEnterPress,
    focusKey: focusKeyParam,
    onArrowPress: onArrowPress,
    extraProps: { focusKey: focusKeyParam },
    focusable: !disabled
  });

  const iconComponent = React.useMemo(() => {
    if (icon) {
      return React.cloneElement(icon as React.ReactElement, {
        className: clsx(styles['icon-button__icon'], (icon as React.ReactElement).props.className),
        size: size
      });
    }
    return null;
  }, [icon, size]);

  const rootClass = clsx(
    styles['icon-button'],
    'icon-button',
    {
      [styles['icon-button--gutter-top']]: gutterTop,
      [styles['icon-button--gutter-bottom']]: gutterBottom,
      [styles['icon-button--gutter-left']]: gutterLeft,
      [styles['icon-button--gutter-right']]: gutterRight,
      [styles['icon-button--push']]: classTrigger === 'push'
    },
    className
  );

  const bodyClass = clsx(styles['icon-button__body'], styles[`icon-button__body--${size}`], {
    [styles['icon-button__body--focused']]: focused,
    [styles['icon-button__body--animating']]: animating,
    [styles['icon-button__body--no-background']]: !focused && background === false
  });

  return (
    <div className={rootClass}>
      <button
        ref={ref}
        type={type}
        className={bodyClass}
        disabled={disabled}
        onAnimationEnd={() => setAnimating(false)}
        {...rest}
      >
        {iconComponent}
      </button>
      <Typography
        color="initial"
        variant="caption"
        className={clsx(styles['icon-button__label'], {
          [styles['icon-button__label--visible']]: focused || activePage
        })}
      >
        {children}
      </Typography>
    </div>
  );
};
